import { Component } from 'react';
import { Autocomplete, Box, Button, Drawer, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { withTranslation } from 'react-i18next';
import '../reports.css';
import { ImagePaths } from '../../../../Utility/ImagePaths';
import RestAPIService from '../../../../Utility/Services/RestAPIService';
import ToastMsg from '../../../../Components/ToastMsg/ToastMsg';
import { CommonGridToolBarWithFilterText, CommonPatientDetails } from "../../../../Components/Common Components/CommonComponents";
import { AmountsCard } from '../../../../Components/CommonCards/CommonCards';
import { Serviceurls } from '../../../../Utility/API/Serviceurls';
import { formatDate } from "../../../../Components/CommonFunctions/CommonFunctions";
import CommonDatePicker from '../../../../Components/Common Components/CommonDatePicker';
import { DateTime } from 'luxon';

/**
 * IP MLC Report – Front Office report for Medical Legal Case (MLC) patients.
 */
class IPMLCReport extends Component {
    /** Initialize state: date range, report data, filters (search, reason/diagnosis), loader and error flags. */
    constructor(props) {
        super(props)
        this.state = {
            page: 0,
            pageSize: 10,
            fromDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
            toDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
            dateIndex: 1,
            mlcReportData: [],
            totalMlcPatients: 0,
            FilterOpen: false,
            SearchedKey: "",
            reasonDiagnosisKey: "",
            reasonDiagnosisOptions: [],
            selectDiagnosis: null,
            isLoader: false,
            isErrorMsg: false,
            isErrorMsgText: ''
        }
    }

    /** Load MLC report data when the component mounts. */
    componentDidMount() {
        this.getMlcReportData()
    }

    /** Set error state and show toast message when an API or validation error occurs. */
    errorMessage = (message) => {
        this.setState({
            isErrorMsg: true,
            isErrorMsgText: message
        })
    }

    /** Clear error toast and reset error state. */
    msgClose() {
        this.setState({
            isErrorMsg: false,
            isErrorMsgText: '',
        })
    }

    /** Toggle global loading indicator (true = show loader, false = hide). */
    LoaderFunction = (key) => {
        this.setState({
            isLoader: key
        })
    }

    /** Fetch Reason/Diagnosis options for the filter dropdown (searchable list from MLC report API). */
    getReasonDiagnosis = (value = '') => {
        try {
            RestAPIService.getAll(Serviceurls.FO_REPORTS_IP_MLC_REPORT + `?search_key=${value}`)
                .then(response => {
                    if (response.data.status === 'success') {
                        this.setState({
                            reasonDiagnosisOptions: response.data ? response.data.data : []
                        })
                    }
                }).catch(e => {
                    if (e.response?.data?.status === "fail") {
                        this.errorMessage(e.response.data.message)
                    } else {
                        this.errorMessage(e.message)
                    }
                })
        } catch (e) {
            this.errorMessage(e.message)
        }
    }

    /** Build query string for MLC report API (from_date, to_date, reason_diagnosis, search). */
    getQueryParams = () => {
        const diagnosisParam = (typeof this.state.selectDiagnosis === 'string' ? this.state.selectDiagnosis : this.state.selectDiagnosis?.reason_diagnosis) || '';
        return `from_date=${this.state.fromDate}&to_date=${this.state.toDate}&reason_diagnosis=${encodeURIComponent(diagnosisParam)}&search=${encodeURIComponent(this.state.SearchedKey || '')}`;
    }

    /** Fetch MLC report list and total count from API; updates table and summary card. */
    getMlcReportData = () => {
        try {
            this.LoaderFunction(true);
            const queryParams = this.getQueryParams();
            RestAPIService.getAll(`${Serviceurls.FO_REPORTS_IP_MLC_REPORT}?${queryParams}`)
                .then((response) => {
                    if (response.data.status === "success") {
                        const data = response.data.data || [];
                        const total = response.data.total_count !== undefined ? response.data.total_count : data.length;
                        this.LoaderFunction(false);
                        this.setState({
                            mlcReportData: data,
                            totalMlcPatients: total
                        })
                    }
                }).catch((error) => {
                    if (error?.response?.data?.status === 'fail') {
                        this.LoaderFunction(false);
                        this.errorMessage(error.response.data.message)
                    } else {
                        this.LoaderFunction(false);
                        this.errorMessage(error.message)
                    }
                })
        } catch (error) {
            this.LoaderFunction(false);
            this.errorMessage(error.message)
        }
    }

    /** Export current report as PDF and open in a new browser tab. */
    mlcReportPrint = () => {
        try {
            const queryParams = this.getQueryParams();
            RestAPIService.getAll(`${Serviceurls.FO_REPORTS_IP_MLC_REPORT}?${queryParams}&export_type=pdf`)
                .then((response) => {
                    if (response && response.data) {
                        const file = new Blob([response.data], { type: 'application/pdf' });
                        const fileURL = URL.createObjectURL(file);
                        window.open(fileURL);
                    }
                }).catch((error) => {
                    if (error?.response?.data?.status === 'fail') {
                        this.errorMessage(error.response.data.message)
                    } else {
                        this.errorMessage(error.message)
                    }
                })
        } catch (error) {
            this.errorMessage(error.message)
        }
    }

    /** Export current report to Excel and trigger file download. */
    getMlcReportExcel = () => {
        try {
            const queryParams = this.getQueryParams();
            RestAPIService.excelGet(`${Serviceurls.FO_REPORTS_IP_MLC_REPORT}?${queryParams}&export_type=excel`)
                .then((response) => {
                    if (response?.data) {
                        const filename = response.headers?.["content-disposition"]?.split("filename=")?.[1] || 'IP_MLC_Report.xlsx';
                        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', filename);
                        link.click();
                        URL.revokeObjectURL(url);
                    }
                }).catch((error) => {
                    if (error?.response?.data?.status === "fail") {
                        this.errorMessage(error.response.data.message)
                    } else {
                        this.errorMessage(error.message)
                    }
                })
        } catch (error) {
            this.errorMessage(error.message)
        }
    }

    /** Close the filter drawer. */
    closeFilter = () => {
        this.setState({ FilterOpen: false })
    }

    /** Render filter drawer: date range, Reason/Diagnosis dropdown, search field, Clear and Search buttons. */
    renderFilterScreen = () => {
        const { t } = this.props;
        console.log(this.state.reasonDiagnosisOptions);
        return (
            <Box component={"div"} className='eMed_rts_filter_container'>
                <Box component={"div"} className='eMed_filter_Top'>
                    <Typography variant='h6' fontWeight={600}>{t("Filter")}</Typography>
                    <Button onClick={() => { this.setState({ FilterOpen: false }) }} className='eMed_usrconf_btn'>
                        <img src={ImagePaths.LabCloseButton.default} alt="close" className='eMed_action_img_small' />
                    </Button>
                </Box>
                <Box className='eMed_rts_middle'>
                    <Box className='eMed_date_filter'>
                        <CommonDatePicker
                            FromDate={this.state.fromDate}
                            ToDate={this.state.toDate}
                            DateIndex={this.state.dateIndex}
                            SelectedDates={this.FilterDate.bind(this)}
                            HideAllMenu={true} />
                    </Box>
                    <Box className='eMed_rts_dropdown'>
                        <Autocomplete
                            noOptionsText={t("NoRecordsFound")}
                            options={this.state.reasonDiagnosisOptions}
                            getOptionLabel={(options) => (typeof options === 'string' ? options : '')}
                            onChange={(e, value) => {
                                this.setState({ selectDiagnosis: value })
                            }}
                            value={this.state.selectDiagnosis}
                            size='small'
                            sx={{ minWidth: '20vw' }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={this.props.t("Reason / Diagnosis")}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        this.setState({ reasonDiagnosisKey: value }, () => { this.getReasonDiagnosis(value) });
                                    }}
                                />
                            )}
                        />
                    </Box>
                    <Box className='eMed_rts_dropdown'>
                        <TextField
                            label={t("Search") + " (Patient Name, UHID)"}
                            sx={{ width: "20vw" }}
                            size='small'
                            value={this.state.SearchedKey}
                            onChange={(e) => this.setState({ SearchedKey: e.target.value })}
                        />
                    </Box>
                </Box>
                <Stack spacing={2} direction="row" alignItems="center" justifyContent="center">
                    <Button variant='outlined' size="small"
                        onClick={() => {
                            this.setState({
                                fromDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
                                toDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
                                dateIndex: 1,
                                SearchedKey: "",
                                reasonDiagnosisKey: "",
                                reasonDiagnosisOptions: [],
                                selectDiagnosis: null,
                            }, () => this.getMlcReportData())
                        }}
                        id="eMed_Config_btn">{t("Clear")}</Button>
                    <Button variant='contained' size="small"
                        onClick={() => {
                            this.getMlcReportData();
                            this.setState({ FilterOpen: false })
                        }}
                        id="eMed_Config_btn">{t("Search")}</Button>
                </Stack>
            </Box>
        )
    }

    /** Toolbar above the grid showing applied filter summary (From/To date, Reason/Diagnosis if set). */
    gridToolBar = () => {
        const TextArray = [
            { label: "From Date", value: formatDate(this.state.fromDate) },
            { label: "To Date", value: formatDate(this.state.toDate) },
        ];
        const diagnosisLabel = typeof this.state.selectDiagnosis === 'string' ? this.state.selectDiagnosis : this.state.selectDiagnosis?.reason_diagnosis;
        if (diagnosisLabel) {
            TextArray.push({ label: "Reason / Diagnosis", value: diagnosisLabel });
        }
        return (
            <Box>
                <CommonGridToolBarWithFilterText FilterTextArray={TextArray} noExport={true} />
            </Box>
        )
    }

    /** Update date range state when user selects From/To dates in the date picker. */
    FilterDate = (from, to, index) => {
        this.setState({
            fromDate: from,
            toDate: to,
            dateIndex: index
        })
    }

    /** Capitalize first letter of each word for display */
    capitalizeText = (str) => {
        if (str == null || str === '') return '-';
        return String(str).toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    }

    render() {
        const rows = (this.state.mlcReportData || []).map((element, index) => ({ ...element, sno: index + 1 }));
        const { t } = this.props;
        const cap = this.capitalizeText;
        /** Uniform cell layout / typography for all grid columns except Patient Details */
        const mlcTruncLen = 15;
        const mlcCellSx = {
            fontSize: '0.8vw',
            width: '100%',
            minHeight: '100%',
            boxSizing: 'border-box',
            px: 1,
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
        };
        const mlcCellCapSx = { ...mlcCellSx, textTransform: 'capitalize' };
        const mlcCellDateSx = { ...mlcCellSx, justifyContent: 'center', textAlign: 'center' };
        const mlcTruncSpanSx = { fontSize: '0.8vw', textTransform: 'capitalize', cursor: 'default' };

        /** Truncate long capitalized text with tooltip (same pattern as police station / comments). */
        const renderMlcTruncatedCapCell = (rawVal) => {
            const display = rawVal ? rawVal : "-";
            if (display.length <= mlcTruncLen) {
                return <Typography component="div" sx={mlcCellCapSx}>{display}</Typography>;
            }
            return (
                <Typography component="div" sx={mlcCellCapSx}>
                    <Tooltip title={display} placement="top" arrow>
                        <Typography component="span" sx={mlcTruncSpanSx}>
                            {display.slice(0, mlcTruncLen) + "..."}
                        </Typography>
                    </Tooltip>
                </Typography>
            );
        };

        const columns = [
            {
                field: "patient_detail",
                headerName: t("Patient Details"),
                flex: 1.5,
                minWidth: 180,
                renderCell: ({ row }) => (
                    <Box component={'div'}>
                        <CommonPatientDetails data={{
                            patient_name: cap(row.patient_name) !== '-' ? cap(row.patient_name) : row.patient_name,
                            patient_uhid: row.uhid || row.patient_uhid,
                            patient__mobile_number: row.mobile || row.patient_mobile_number,
                            patient_mobile_number: row.mobile || row.patient_mobile_number,
                            age: row.age,
                            gender: row.gender ? cap(row.gender) : row.gender,
                        }} hideInsurance={true} disablePhoto={true} />
                    </Box>
                ),
                valueGetter: (params) => {
                    const name = params.row?.patient_name || "";
                    const mobile = params.row?.mobile || params.row?.patient_mobile_number || "";
                    const uhid = params.row?.uhid || params.row?.patient_uhid || "";
                    return `${name} ${mobile} ${uhid}`;
                }
            },
            {
                field: "admission_date_time",
                headerName: t("Admission Date & Time"),
                flex: 1,
                minWidth: 130,
                headerAlign: "center",
                align: "center",
                renderCell: (params) => (
                    <Typography component="div" sx={mlcCellSx}>
                        {params?.row?.admission_date_time || "-"}
                    </Typography>
                ),
                valueGetter: (params) => params.row?.admission_date_time || ''
            },
            {
                field: "reason_diagnosis",
                headerName: t("Reason / Diagnosis"),
                flex: 1,
                minWidth: 130,
                renderCell: (params) => (
                    <Typography component="div" sx={mlcCellCapSx}>
                        {cap(params?.row?.reason_diagnosis || params?.row?.diagnosis || params?.row?.["reason/diagnosis"] || "-")}
                    </Typography>
                )
            },
            {
                field: "incident_date_time",
                headerName: t("Incident Date & Time"),
                flex: 1,
                minWidth: 130,
                headerAlign: "center",
                align: "center",
                renderCell: (params) => (
                    <Typography component="div" sx={mlcCellDateSx}>
                        {params?.row?.incident_date_time || "-"}
                    </Typography>
                ),
                valueGetter: (params) => params.row?.incident_date_time || ''
            },
            {
                field: "incident_location",
                headerName: t("Incident Location"),
                flex: 1,
                minWidth: 150,
                renderCell: (params) => renderMlcTruncatedCapCell(params?.row?.incident_location)
            },
            {
                field: "informer_name",
                headerName: t("Informer Name"),
                flex: 1,
                minWidth: 150,
                renderCell: (params) => renderMlcTruncatedCapCell(params?.row?.informer_name)
            },
            {
                field: "police_station",
                headerName: t("Police Station"),
                flex: 1,
                minWidth: 150,
                renderCell: (params) => renderMlcTruncatedCapCell(params?.row?.police_station)
            },
            {
                field: "comments",
                headerName: t("Comments"),
                flex: 1,
                minWidth: 150,
                renderCell: (params) => renderMlcTruncatedCapCell(params?.row?.comments)
            },
        ];

        return (
            <Box component={'div'} className='eMed_rts_container'>
                <Box component={"div"} className='eMed_Top_Container'>
                    <Box component={"div"} className='eMed_Cards_Container'>
                        {AmountsCard(t("Total MLC Patients"), this.state.totalMlcPatients, true)}
                    </Box>
                    <Box component={"div"} className='eMed_Action_Container'>
                        <Box component={"div"} className='eMed_Filter_Icon_div'>
                            <Button className='eMed_usrconf_btn' onClick={() => { this.setState({ FilterOpen: true }) }}>
                                <img src={ImagePaths.Filter.default} alt="filter" className='eMed_action_img' />
                            </Button>
                        </Box>
                        <Button className='eMed_usrconf_btn' onClick={() => this.mlcReportPrint()}>
                            <img src={ImagePaths.LabPrintIcon.default} alt="print" className='eMed_action_img' />
                        </Button>
                        <Button className='eMed_usrconf_btn' onClick={() => this.getMlcReportExcel()}>
                            <img src={ImagePaths.ExportIcon.default} alt="export" className='eMed_action_img' />
                        </Button>
                    </Box>
                </Box>
                <Box component={'div'} className='eMed_rts_table'>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => row.id ?? row.sno}
                        page={this.state.page}
                        pageSize={this.state.pageSize}
                        density='compact'
                        rowHeight={90}
                        localeText={{
                            toolbarColumns: "",
                            toolbarDensity: "",
                            toolbarExport: "",
                            toolbarFilters: "",
                            toolbarExportPrint: ""
                        }}
                        components={{
                            Toolbar: this.gridToolBar,
                            NoRowsOverlay: () => (
                                <Stack className='eMed_conf_nodata'>
                                    {t("NoRecordsFound")}
                                </Stack>
                            )
                        }}
                        onPageChange={(newPage) => this.setState({ page: newPage })}
                        onPageSizeChange={(newPageSize) => this.setState({ pageSize: newPageSize })}
                        rowsPerPageOptions={[10, 20, 30]}
                        pagination
                        disableSelectionOnClick
                        loading={this.state.isLoader}
                    />
                </Box>
                <Drawer
                    anchor={'right'}
                    open={this.state.FilterOpen}
                    onClose={this.closeFilter}
                    ModalProps={{ onBackdropClick: this.closeFilter }}
                >
                    {this.renderFilterScreen()}
                </Drawer>
                {this.state.isErrorMsg && (
                    <ToastMsg
                        severity={'error'}
                        msg={this.state.isErrorMsgText}
                        msgPop={this.msgClose.bind(this)}
                    />
                )}
            </Box>
        )
    }
}

export default withTranslation()(IPMLCReport)
